/**
 * Standard Reports — pre-built, parameterised, schedulable reports.
 *
 * These types mirror what `API_v5` actually returns. They deliberately replace
 * the deleted `reports.types.ts`, which described ten analytics endpoints that
 * never existed — the shapes below are taken from live controller responses,
 * not from documentation.
 *
 * MONEY IS A STRING, EVERYWHERE. Payment amounts are stored as `float(20,2)`
 * server-side, so every total in this feature is carried as an exact decimal
 * string from SQL through to the sheet. Parsing one into a `number` here to
 * "tidy it up" reintroduces exactly the drift that chain exists to prevent — in
 * a report whose entire purpose is that a total ties out.
 */

/** A column as the report declares it; drives both rendering and alignment. */
export type StandardReportColumnType = 'text' | 'money' | 'date' | 'int';

export interface StandardReportColumn {
  key: string;
  label: string;
  type: StandardReportColumnType;
}

/**
 * How a section relates to the closing total.
 *
 * `held_out` sections print their own subtotal but contribute nothing — they
 * exist so a reader can see what was excluded and how much of it there was.
 */
export type StandardReportSectionKind = 'flat' | 'batch' | 'held_out';

export interface StandardReportSection {
  key: string;
  label: string;
  kind: StandardReportSectionKind;
  /** Exact decimal string, or null when the section prints no subtotal. */
  subtotal: string | null;
  countsTowardTotal: boolean;
  /** Rows the section holds in full, which may exceed `rows.length`. */
  totalRowCount: number;
  rows: Array<Record<string, unknown>>;
  /**
   * Facts about the block itself, printed under its heading — what the gateway
   * says a payout was worth, and how our rows compare. No column can carry
   * these because they describe the batch, not any row in it. Absent on every
   * section that has nothing of the kind to say.
   */
  notes?: Array<{ label: string; value: string }>;
}

/**
 * The stored preview of a run — a byte-bounded slice, not the whole result.
 *
 * When a run is truncated this holds fewer rows than it counted, and the screen
 * must say so. The workbook is always the complete record.
 */
export interface StandardReportPreview {
  columns: StandardReportColumn[];
  sections: StandardReportSection[];
  grandTotal: string | null;
  grandTotalLabel: string;
  headerNotes: Array<{ label: string; value: string }>;
}

/** A catalog entry. Deliberately carries no dataset or transform key. */
export interface StandardReportDescriptor {
  key: string;
  title: string;
  category: string;
  summary: string;
  description?: string;
  filterKeys: string[];
  /**
   * The period the runner page and the schedule dialog start on. Null or
   * absent means the shared default, last month.
   */
  defaultDatePreset?: string | null;
  /**
   * A report with its own page (the Marketing Flex Table): the catalog card
   * opens this path instead of the runner, and the entry has no dataset.
   */
  route?: string;
}

/**
 * Run states.
 *
 * `empty` and `too_large` are ANSWERS, not failures: the first means the
 * filters matched nothing, the second that the result exceeded the storage cap
 * and nothing was delivered. Rendering either as an error would tell a user
 * something broke when nothing did.
 */
export type StandardReportRunStatus =
  | 'pending'
  | 'processing'
  | 'delivering'
  | 'ready'
  | 'empty'
  | 'too_large'
  | 'failed';

export const TERMINAL_RUN_STATUSES: StandardReportRunStatus[] = [
  'ready',
  'empty',
  'too_large',
  'failed',
];

export function isTerminalRunStatus(status: StandardReportRunStatus): boolean {
  return TERMINAL_RUN_STATUSES.includes(status);
}

export interface StandardReportRecipientOutcome {
  address: string;
  /** `pending` means never attempted — distinct from `failed`. */
  state: 'pending' | 'sent' | 'failed' | 'skipped';
  attemptedAt: string | null;
  failureReason: string | null;
}

export interface StandardReportSectionTotal {
  key: string;
  label: string;
  kind: StandardReportSectionKind;
  subtotal: string | null;
  rowCount: number;
  countsTowardTotal: boolean;
}

export interface StandardReportRun {
  runId: number;
  /** The saved order that produced this run, or null for a one-off catalog run. */
  orderId: number | null;
  reportKey: string;
  status: StandardReportRunStatus;
  /** The window the run RESOLVED, never re-derived from a relative key. */
  windowStart: string | null;
  windowEnd: string | null;
  sectionTotals: StandardReportSectionTotal[] | null;
  /** Exact decimal string — never parse this into a number. */
  grandTotal: string | null;
  rowCount: number | null;
  truncated: boolean;
  purged: boolean;
  hasFile: boolean;
  /**
   * Why the run refused, when it did — safe to render verbatim. Names the row
   * count and what to change. Null for every other status: the server never
   * puts a phase name or an exception class in here, so a failed run has
   * nothing to show and should not invent one.
   */
  refusalReason: string | null;
  createdAt: string | null;
  completedAt: string | null;
  recipients: StandardReportRecipientOutcome[];
  /** Present only on the single-run read. */
  preview?: StandardReportPreview | null;
}

export interface StandardReportRunStatusPoll {
  runId: number;
  status: StandardReportRunStatus;
  rowCount: number | null;
  truncated: boolean;
  completedAt: string | null;
  /** A phase name and exception class only — never a message or a filter value. */
  detail: string | null;
}

/**
 * Report parameters.
 *
 * `datePreset` and explicit bounds are mutually exclusive, and the server
 * rejects both together. An ORDER stores the preset key so each fire covers the
 * period that just closed; a RUN stores resolved bounds so reopening it later
 * still names the period it actually covered.
 */
/**
 * A standing order — a recurring delivery.
 *
 * `datePreset` is a KEY, not a resolved range, and that distinction is the
 * whole point: the server re-resolves it on every fire, so each delivery covers
 * the period that just closed. Resolving it here and storing bounds would
 * freeze the order on one window, and it would fail silently — the file still
 * arrives, on time, covering the wrong period.
 */
export interface StandardReportOrder {
  orderId: number;
  title: string | null;
  reportKey: string;
  datePreset: string | null;
  /** Free-text list, exactly as the user typed it. */
  recipients: string;
  recurring: boolean;
  cadenceUnit: 'day' | 'week' | 'month' | 'year' | null;
  cadenceCount: number;
  timezone: string | null;
  timeOfDay: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastRunId: number | null;
  runCount: number;
  active: boolean;
  ownerUserId: number | null;
  /** Why the scheduler paused it — e.g. the report left the allow-list. */
  pausedReason: string | null;
}

export interface StandardReportOrderInput {
  title?: string;
  reportKey: string;
  params: StandardReportParams;
  recipients?: string;
  recurring?: boolean;
  cadenceUnit?: 'day' | 'week' | 'month' | 'year';
  cadenceCount?: number;
  timezone?: string;
  timeOfDay?: string;
  nextRunAt?: string;
  active?: boolean;
  /**
   * Required when any recipient is outside the practice's mail domain. The
   * server refuses the save without it rather than silently mailing patient
   * data outside the organisation.
   */
  confirmExternalRecipients?: boolean;
}

export interface StandardReportParams {
  datePreset?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  facilityIds?: number[];
  paymentTypes?: string[];
  statuses?: string[];
  planTypeIds?: number[];
  productIds?: number[];
  templateIds?: number[];
  /** Lead source ids, or `filtersgroup-<id>` for a whole category. */
  leadSourceIds?: string[];
  packageIds?: number[];
  membershipIds?: number[];
  doctorIds?: number[];
  coordIds?: number[];
  userIds?: number[];
}
